/**
 * 主要用于报表的图表卡片右上角 展示 过滤条件的浮窗
 */
import React, { useEffect, useMemo, useState } from "react";
import { Bubble, BubbleProps } from "tea-component";
import SvgIcon from "@home/components/SvgIcon";
import classNames from "classnames";
import { forOwn } from "lodash-es";
import { GeneralFilterList, UserFilterControlList } from "@/pages/datalab/components/BubbleListContent/AllBubbleList";

/**
 * 通用过滤项类型
 */
interface IGeneralFilterItem {
  scopeTextCn?: string;
  scopeTextEn?: string;
}

/**
 * 用户过滤控制项类型
 */
interface IUserFilterControlItem {
  nameEn?: string;
  nameCn?: string;
}

/**
 * GeneralFilterList 组件 Props
 */
interface IGeneralFilterListProps {
  filters?: IGeneralFilterItem[];
}

/**
 * UserFilterControlList 组件 Props
 */
interface IUserFilterControlListProps {
  controls?: IUserFilterControlItem[];
}

/**
 * ContentMap 的 key 常量
 * 提供给外部使用，避免硬编码字符串
 */
export const FILTER_CONTENT_KEY = {
  GENERAL_FILTER: 'generalFilter',
  USER_FILTER: 'userFilter',
} as const;

/**
 * ContentMap 的 key 类型
 */
export type TContentMapKey = typeof FILTER_CONTENT_KEY[keyof typeof FILTER_CONTENT_KEY];

/**
 * 组件映射对象
 * 添加新组件时，contentMap 的 key 类型会自动更新
 */
const ListComponentMap = {
  [FILTER_CONTENT_KEY.GENERAL_FILTER]: GeneralFilterList,
  [FILTER_CONTENT_KEY.USER_FILTER]: UserFilterControlList,
};

/**
 * ContentMap 中每个 key 对应的 value 类型
 */
type TContentMapValue = IGeneralFilterListProps | IUserFilterControlListProps;

/**
 * ContentMap 类型定义
 * key 自动从 ListComponentMap 中提取，确保类型安全
 */
export type TContentMap = Partial<Record<keyof typeof ListComponentMap, TContentMapValue>>;

/**
 * 检查 value 是否有效（不是 undefined 或 null，且是对象类型）
 */
const isValidValue = (value: unknown): value is Record<string, unknown> => {
  return value !== undefined && value !== null && typeof value === 'object' && !Array.isArray(value);
};

/**
 * ShowFilterContentIcon 组件 Props
 */
interface IShowFilterContentIconProps extends Omit<BubbleProps, 'content'> {
  filters?: unknown;
  userFilter?: unknown;
  isEdit?: boolean;
  contentMap?: TContentMap;
  /** 与漏斗图标同一悬停触发区（如预览「更多」里整行「当前筛选」） */
  triggerEndContent?: React.ReactNode;
  /**
   * 长按才显示（按住显示、松开关闭）。
   * 用于预览三点菜单内「当前筛选」：避免 hover 经过就弹出干扰菜单操作。
   */
  pressToShow?: boolean;
}

const ShowFilterContentIcon: React.FC<IShowFilterContentIconProps> = (props) => {
  const { isEdit, contentMap, overlayClassName, triggerEndContent, pressToShow, onVisibleChange, ...rest } = props;
  const [pressVisible, setPressVisible] = useState(false);
  const visible = pressToShow ? pressVisible : rest.visible;
  /**
   * 根据 contentMap 生成组件列表
   * 从 ListComponentMap 中获取对应组件，并将 contentMap 的 value 作为 props 传递
   */
  const bubbleContent = useMemo((): React.ReactNode => {
    if (!contentMap || typeof contentMap !== 'object') {
      return undefined;
    }
    const validComponents: React.ReactElement[] = [];
    forOwn(contentMap, (value, key) => {
      // 检查 value 是否有效
      if (!isValidValue(value)) {
        return;
      }
      // 类型守卫：确保 key 是有效的组件键
      if (!(key in ListComponentMap)) {
        return;
      }
      // 从 ListComponentMap 中获取对应组件
      const Component = ListComponentMap[key as TContentMapKey];
      // 创建组件实例，将 value 作为 props 传递
      const componentElement = (
        <Component
          key={key}
          {...(value as TContentMapValue)}
        />
      );
      
      validComponents.push(componentElement);
    });
    
    // 如果没有有效组件，返回 undefined
    if (validComponents.length === 0) {
      return undefined;
    }
    
    // 返回包含所有组件的 Fragment
    return <>{validComponents}</>;
  }, [contentMap]);
  
  const iconBlock = (
    <div className={classNames('dbl-card-icon-download', {'dbl-card-icon-show': !isEdit})}>
      <SvgIcon type="funnel"/>
    </div>
  );

  // 长按模式：鼠标/触屏抬起时必须关闭（哪怕在触发区外抬起）
  useEffect(() => {
    if (!pressToShow || !pressVisible) return;
    const end = (): void => {
      setPressVisible(false);
      onVisibleChange?.(false);
    };
    window.addEventListener('mouseup', end);
    window.addEventListener('touchend', end);
    window.addEventListener('touchcancel', end);
    return () => {
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchend', end);
      window.removeEventListener('touchcancel', end);
    };
  }, [onVisibleChange, pressToShow, pressVisible]);

  const triggerProps = pressToShow
    ? {
      trigger: 'empty' as const,
      visible,
      onVisibleChange,
    }
    : {
      trigger: 'hover' as const,
      onVisibleChange,
    };

  return (
    <Bubble
      content={bubbleContent}
      dark
      placement={'left'}
      placementOffset={[8, 25]}
      overlayClassName={classNames('dbl-dashboard-filter-info-bubble', 'dbl-show-filter-overlay', overlayClassName)}
      {...rest}
      {...triggerProps}
    >
      {triggerEndContent !== null ? (
        <div
          className="dbl-show-filter-bubble-trigger dbl-show-filter-bubble-trigger--with-end"
          onMouseDown={() => {
            if (!pressToShow) return;
            if (!bubbleContent) return;
            setPressVisible(true);
            onVisibleChange?.(true);
          }}
          onMouseLeave={() => {
            if (!pressToShow) return;
            setPressVisible(false);
            onVisibleChange?.(false);
          }}
          onTouchStart={() => {
            if (!pressToShow) return;
            if (!bubbleContent) return;
            setPressVisible(true);
            onVisibleChange?.(true);
          }}
        >
          {iconBlock}
          {triggerEndContent}
        </div>
      ) : (
        <div
          onMouseDown={() => {
            if (!pressToShow) return;
            if (!bubbleContent) return;
            setPressVisible(true);
            onVisibleChange?.(true);
          }}
          onMouseLeave={() => {
            if (!pressToShow) return;
            setPressVisible(false);
            onVisibleChange?.(false);
          }}
          onTouchStart={() => {
            if (!pressToShow) return;
            if (!bubbleContent) return;
            setPressVisible(true);
            onVisibleChange?.(true);
          }}
        >
          {iconBlock}
        </div>
      )}
    </Bubble>
  );
};

export default ShowFilterContentIcon;